import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';

import axios from 'axios';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AnnonceItem from '../../components/AnnonceItem';
import AutocompleteMapInput from '../../components/AutocompleteMapInput';
import MapItem from '../../components/MapItem';
import McDateRangePicker from '../../components/McDateRangePicker';
import env from '../../config/env';
import '../../styles/style.css';
import { useAxios } from '../../utils/hook/useAxios';
import { useUser } from '../../utils/hook/UserContext';

/* ==========================================================
 * CONSTANTES / HELPERS (hors composant : créés une seule fois)
 * ========================================================== */

const PAGE_SIZE = 20;

function parseSearchDate(value) {
  if (!value) return null;

  const parts = value.split(' ');

  if (parts.length >= 6) {
    const formatted = parts.slice(1, 4).join(' ') + ' ' + parts[4] + ' ' + parts[5];
    const parsed = dayjs(formatted);

    return parsed.isValid() ? parsed : null;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed : null;
}

function readLocalStorageJson(key, fallback) {
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

const buildSearchUrl = ({ address, center, start, end }) =>
  `/search?address=${encodeURIComponent(address || '')}` +
  `&center=${encodeURIComponent(center.join(','))}` +
  `&address_source=mapbox` +
  `&longitude=${center[0]}` +
  `&latitude=${center[1]}` +
  `&start=${encodeURIComponent(new Date(start).toString())}` +
  `&end=${encodeURIComponent(new Date(end).toString())}`;

const deg2rad = (deg) => deg * (Math.PI / 180);

function getDistanceKm(lat1, lon1, lat2, lon2) {
  const earthRadius = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/*
 * ----------------------------------------------------------
 * MODE DE SECOURS (ancienne API)
 * ----------------------------------------------------------
 * Si le backend renvoie encore un simple tableau (ancien
 * endpoint), on filtre / trie / pagine côté client pour que la
 * page continue de fonctionner. Une fois le nouvel endpoint
 * déployé, ce code n'est plus utilisé.
 */
function legacyProcess(list, { origin, days, sortParam, brand, minAcceptance }) {
  return list
    .filter((a) => String(a.status) === '1')
    .map((a) => {
      let distance = 0;

      if (a.locationCoordinates) {
        const [lat, lng] = a.locationCoordinates.split(',');
        distance = getDistanceKm(origin.lat, origin.lng, parseFloat(lat), parseFloat(lng));
      }

      return { ...a, distance: Number(distance.toFixed(2)), nombreJours: days };
    })
    .filter(
      (a) => !minAcceptance || Number(a?.Vehicle?.UserProfile?.AcceptanceRate) >= minAcceptance
    )
    .filter((a) => {
      if (brand === '0') return !a?.Vehicle?.IsUtilitaire;
      if (brand === '1') return Boolean(a?.Vehicle?.IsUtilitaire);
      return true;
    })
    .sort((a, b) => {
      if (sortParam === 'price_asc') return Number(a.reservationPrice) - Number(b.reservationPrice);
      if (sortParam === 'price_desc')
        return Number(b.reservationPrice) - Number(a.reservationPrice);
      if (sortParam === 'distance_desc') return b.distance - a.distance;
      return a.distance - b.distance;
    });
}

/* ========================================================== */

function SearchResultPage() {
  const AxiosRequest = useAxios();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useUser();

  const currentUserId = user?.id ?? readLocalStorageJson('me', null)?.id ?? null;

  /*
   * ----------------------------------------------------------
   * L'URL EST LA SOURCE DE VÉRITÉ
   * ----------------------------------------------------------
   * Adresse, coordonnées et dates sont lues depuis l'URL.
   * Choisir une adresse / des dates = navigate(), et UN SEUL
   * effet recharge les annonces (plus de double appel).
   */
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const urlLatitude = searchParams.get('latitude') || '';
  const urlLongitude = searchParams.get('longitude') || '';
  const urlAddress = searchParams.get('address') || '';
  const urlStart = searchParams.get('start');
  const urlEnd = searchParams.get('end');

  const urlCenter = useMemo(() => {
    if (urlLatitude === '' || urlLongitude === '') return null;

    const lng = Number(urlLongitude);
    const lat = Number(urlLatitude);

    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
  }, [urlLatitude, urlLongitude]);

  const searchStart = useMemo(() => parseSearchDate(urlStart), [urlStart]);
  const searchEnd = useMemo(() => parseSearchDate(urlEnd), [urlEnd]);

  const searchStartDate = useMemo(() => (searchStart ? searchStart.toDate() : null), [searchStart]);
  const searchEndDate = useMemo(() => (searchEnd ? searchEnd.toDate() : null), [searchEnd]);

  const days = useMemo(() => {
    if (!searchStart || !searchEnd) return 1;
    return Math.max(1, Math.floor(searchEnd.diff(searchStart, 'hours') / 24));
  }, [searchStart, searchEnd]);

  /*
   * ----------------------------------------------------------
   * ÉTATS
   * ----------------------------------------------------------
   */

  const [selectedDates, setSelectedDates] = useState([searchStart, searchEnd]);

  // Adresse choisie dans l'autocomplete mais pas encore appliquée (mobile)
  const [pendingLocation, setPendingLocation] = useState(null);

  // Liste paginée
  const [annonces, setAnnonces] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filtres (envoyés au serveur)
  const [sortOrder, setSortOrder] = useState('1');
  const [sortKm, setSortKm] = useState('1');
  /*
   * null = tous | "0" = citadin | "1" = utilitaire | "2" = SUV | "3" = monospace
   */
  const [brand, setBrand] = useState(null);
  const [isChecked, setIsChecked] = useState(false);

  const [isChanged, setIsChanged] = useState(false);

  // null | location | filters | selected
  const [activePanel, setActivePanel] = useState(null);
  // null | moreFilter | fullScreenMap | reservation
  const [activeModal, setActiveModal] = useState(null);

  const [selectedVoitures, setSelectedVoitures] = useState(() =>
    readLocalStorageJson('selectedVoitures', [])
  );
  const [personnalisedMessage, setPersonnalisedMessage] = useState('');

  const [favoriteIds, setFavoriteIds] = useState(() => new Set());

  const abortRef = useRef(null);
  const legacyRef = useRef(null);
  const sentinelRef = useRef(null);

  /* Synchronise les dates avec l'URL (retour arrière, lien partagé…) */
  useEffect(() => {
    setSelectedDates([searchStart, searchEnd]);
  }, [searchStart, searchEnd]);

  /* Notifie la carte quand la position change */
  useEffect(() => {
    setIsChanged((previous) => !previous);
  }, [urlLatitude, urlLongitude]);

  /*
   * ----------------------------------------------------------
   * PANNEAUX / MODALES
   * ----------------------------------------------------------
   */

  const openPanel = (panel) => {
    setActiveModal(null);
    setActivePanel(panel);
  };

  const closePanel = () => setActivePanel(null);

  const openModal = (modal) => {
    setActivePanel(null);
    setActiveModal(modal);
  };

  const closeModal = () => setActiveModal(null);

  useEffect(() => {
    const isOpen = activePanel !== null || activeModal !== null;

    document.body.style.overflow = isOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [activePanel, activeModal]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;

      setActivePanel(null);
      setActiveModal(null);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  /*
   * ----------------------------------------------------------
   * RECHERCHE (navigation)
   * ----------------------------------------------------------
   */

  const goSearch = useCallback(
    (loc, dates) => {
      if (!loc?.center || !dates?.[0] || !dates?.[1]) return false;
      if (!loc.center[0] || !loc.center[1]) return false;

      navigate(
        buildSearchUrl({ address: loc.name, center: loc.center, start: dates[0], end: dates[1] })
      );

      return true;
    },
    [navigate]
  );

  const currentLocation =
    pendingLocation ?? (urlCenter ? { center: urlCenter, name: urlAddress } : null);

  const handleLocationSelection = (locationAddress) => {
    if (!locationAddress) return;

    const center = locationAddress.location || locationAddress.center;

    if (!center) return;

    const next = {
      center,
      name: locationAddress.name || locationAddress.place_name || '',
    };

    setPendingLocation(next);

    // Desktop : pas de bouton "Appliquer" => on lance la recherche directement
    if (activePanel === null) {
      goSearch(next, selectedDates);
    }
  };

  const handleDateChange = (newDates) => {
    setSelectedDates(newDates);
    goSearch(currentLocation, newDates);
  };

  const handleSearch = () => {
    goSearch(currentLocation, selectedDates);
    closePanel();
  };

  /*
   * ----------------------------------------------------------
   * FILTRES
   * ----------------------------------------------------------
   */

  const handleSortChange = (event) => setSortOrder(event.target.value);

  const handleSortKmChange = (event) => setSortKm(event.target.value);

  const handleBrandChange = (event) => {
    const value = event.target.value;
    setBrand(value === '99' ? null : value);
  };

  const handleCheckboxChange = (event) => setIsChecked(event.target.checked);

  const sortParam =
    sortOrder === '2'
      ? 'price_asc'
      : sortOrder === '3'
        ? 'price_desc'
        : sortKm === '3'
          ? 'distance_desc'
          : 'distance_asc';

  const hasFilter = sortOrder !== '1' || sortKm !== '1' || brand !== null || isChecked;

  /*
   * ----------------------------------------------------------
   * CHARGEMENT PAGINÉ DES ANNONCES
   * ----------------------------------------------------------
   * UNE requête par page (20 annonces). Le serveur renvoie déjà :
   * distance, note, nb d'avis, mode instantané, horaires hôte,
   * indisponibilité. Plus aucun appel dans les cartes.
   */

  const loadPage = useCallback(
    async (pageToLoad) => {
      if (!urlCenter) {
        setAnnonces([]);
        setTotal(0);
        setHasMore(false);
        setIsLoading(false);
        return;
      }

      // Annule la requête précédente (évite qu'une vieille réponse écrase la nouvelle)
      abortRef.current?.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      if (pageToLoad === 1) setIsLoading(true);
      else setLoadingMore(true);

      try {
        // Pagination locale (mode de secours uniquement)
        if (pageToLoad > 1 && legacyRef.current) {
          const all = legacyRef.current;
          const next = all.slice(0, pageToLoad * PAGE_SIZE);

          setAnnonces(next);
          setHasMore(next.length < all.length);
          setPage(pageToLoad);
          return;
        }

        const { data } = await axios.get(`${env.baseUrlApi}/vehicleAnnonces/search-listing`, {
          signal: controller.signal,
          params: {
            latitude: urlCenter[1],
            longitude: urlCenter[0],
            page: pageToLoad,
            limit: PAGE_SIZE,
            sort: sortParam,
            vehicleType: brand ?? undefined,
            minAcceptance: isChecked ? 30 : undefined,
            start: searchStart ? searchStart.toISOString() : undefined,
            end: searchEnd ? searchEnd.toISOString() : undefined,
          },
        });

        if (Array.isArray(data)) {
          // Ancien backend : tableau complet
          const all = legacyProcess(data, {
            origin: { lat: urlCenter[1], lng: urlCenter[0] },
            days,
            sortParam,
            brand,
            minAcceptance: isChecked ? 30 : 0,
          });

          legacyRef.current = all;

          setAnnonces(all.slice(0, PAGE_SIZE));
          setTotal(all.length);
          setHasMore(all.length > PAGE_SIZE);
          setPage(1);
          return;
        }

        legacyRef.current = null;

        const items = (data?.items ?? []).map((annonce) => ({ ...annonce, nombreJours: days }));

        setAnnonces((previous) => (pageToLoad === 1 ? items : [...previous, ...items]));
        setTotal(data?.total ?? items.length);
        setHasMore(Boolean(data?.hasMore));
        setPage(pageToLoad);
      } catch (error) {
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return;

        console.error('Erreur lors de la requête Axios :', error);

        if (pageToLoad === 1) {
          setAnnonces([]);
          setTotal(0);
          setHasMore(false);
        }

        toast.error('Impossible de charger les annonces.');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [urlCenter, sortParam, brand, isChecked, searchStart, searchEnd, days]
  );

  /* UN SEUL effet de chargement : se relance si l'URL ou un filtre change */
  useEffect(() => {
    legacyRef.current = null;
    loadPage(1);

    return () => abortRef.current?.abort();
  }, [loadPage]);

  /* Scroll infini */
  useEffect(() => {
    if (!hasMore || loadingMore || isLoading || !sentinelRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadPage(page + 1);
      },
      { rootMargin: '400px' }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, loadingMore, isLoading, page, loadPage]);

  /*
   * ----------------------------------------------------------
   * FAVORIS (1 seule requête pour toute la liste)
   * ----------------------------------------------------------
   */

  useEffect(() => {
    if (!currentUserId) return;

    let cancelled = false;

    AxiosRequest.get(`/favorites/user/${currentUserId}`)
      .then((response) => {
        if (cancelled || !Array.isArray(response.data)) return;

        setFavoriteIds(
          new Set(response.data.map((favorite) => favorite.vehiculeAnnonceId ?? favorite))
        );
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  const redirectToSignin = useCallback(() => {
    window.location.href = `/signin?redirect=${encodeURIComponent(
      window.location.pathname + window.location.search
    )}`;
  }, []);

  const toggleFavorite = useCallback(
    async (annonceId) => {
      if (!currentUserId) {
        redirectToSignin();
        return;
      }

      // Mise à jour optimiste
      setFavoriteIds((previous) => {
        const next = new Set(previous);

        if (next.has(annonceId)) next.delete(annonceId);
        else next.add(annonceId);

        return next;
      });

      try {
        await AxiosRequest.post('/favorites/toggle', {
          userId: currentUserId,
          vehiculeAnnonceId: annonceId,
        });
      } catch (error) {
        console.error('Erreur favori :', error);

        // Annule la mise à jour optimiste
        setFavoriteIds((previous) => {
          const next = new Set(previous);

          if (next.has(annonceId)) next.delete(annonceId);
          else next.add(annonceId);

          return next;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserId, redirectToSignin]
  );

  /*
   * ----------------------------------------------------------
   * VOITURES SÉLECTIONNÉES ("Mes demandes")
   * ----------------------------------------------------------
   * Le parent possède la sélection : les cartes reçoivent juste
   * un booléen `isSelected`, plus aucun état local désynchronisé.
   */

  useEffect(() => {
    try {
      localStorage.setItem('selectedVoitures', JSON.stringify(selectedVoitures));
    } catch {
      /* stockage indisponible */
    }
  }, [selectedVoitures]);

  const selectedIds = useMemo(
    () => new Set(selectedVoitures.map((voiture) => voiture.id_de_la_voiture)),
    [selectedVoitures]
  );

  const toggleSelect = useCallback(
    (annonce) => {
      const vehicle = annonce?.Vehicle;

      if (!vehicle) return;

      if (!searchStartDate || !searchEndDate) {
        navigate(`/location-voiture/${annonce.vehiculeAnnonceId}`);
        return;
      }

      setSelectedVoitures((previous) => {
        if (previous.some((voiture) => voiture.id_de_la_voiture === vehicle.id)) {
          return previous.filter((voiture) => voiture.id_de_la_voiture !== vehicle.id);
        }

        return [
          ...previous,
          {
            dateEntree: searchStartDate.toISOString(),
            dateSortie: searchEndDate.toISOString(),
            prix_par_jour: annonce.reservationPrice,
            nombre_de_jours: (searchEndDate.getTime() - searchStartDate.getTime()) / 86400000,
            id_de_la_voiture: vehicle.id,
            image_principal: vehicle.images?.split(',')[0],
            driverId: vehicle.userId,
            vehiculeAnnonceId: annonce.vehiculeAnnonceId,
          },
        ];
      });
    },
    [searchStartDate, searchEndDate, navigate]
  );

  const removeSelected = (vehicleId) => {
    setSelectedVoitures((previous) =>
      previous.filter((voiture) => voiture.id_de_la_voiture !== vehicleId)
    );
  };

  /* Appelé par la carte (MapItem) qui écrit directement dans le localStorage */
  const refreshSelection = useCallback(() => {
    setSelectedVoitures(readLocalStorageJson('selectedVoitures', []));
  }, []);

  /*
   * ----------------------------------------------------------
   * ENVOI DE DEMANDE
   * ----------------------------------------------------------
   */

  async function handleReserver() {
    if (!isAuthenticated) {
      closeModal();
      redirectToSignin();
      return;
    }

    if (selectedVoitures.length === 0) {
      toast.error('Aucune voiture sélectionnée.');
      return;
    }

    try {
      localStorage.setItem('personnalisedMessage', personnalisedMessage || '');

      let successCount = 0;

      for (let j = 0; j < selectedVoitures.length; j += 1) {
        const voiture = selectedVoitures[j];

        try {
          // Le backend détermine driverInviteId depuis req.user.id
          const response = await AxiosRequest.post(`/reservations`, {
            driverHoteId: voiture.driverId,
            vehiculeId: voiture.id_de_la_voiture,
            startDate: voiture.dateEntree,
            endDate: voiture.dateSortie,
            vehiculeAnnonceId: voiture.vehiculeAnnonceId,
          });

          try {
            await AxiosRequest.post(`/conversations`, {
              authorDriverRole: 1,
              driversId: voiture.driverId,
              sentTime: new Date(),
              reservationId: response.data.reservationId,
              message: personnalisedMessage || 'Nouvelle demande de réservation',
            });

            // Corrigé : utilisait axios (sans baseURL ni token) au lieu de AxiosRequest
            await AxiosRequest.post(`/reservationsGain`, {
              reservationId: response.data.reservationId,
              amount: Number(voiture.prix_par_jour) * Number(voiture.nombre_de_jours),
              type: '0',
            });
          } catch (innerError) {
            console.error('Erreur conversation/gain :', innerError);
          }

          successCount += 1;
        } catch (error) {
          const msg =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Une erreur est survenue. Veuillez réessayer.';

          if (error?.response?.status === 409) {
            toast.error(`🚫 ${msg}`, { autoClose: 6000 });
          } else {
            toast.error(msg);
          }
        }
      }

      if (successCount > 0) {
        localStorage.removeItem('selectedVoitures');

        setSelectedVoitures([]);
        setPersonnalisedMessage('');

        // On remplace la modale de confirmation par la modale de succès
        openModal('success');
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi des demandes :", error);

      toast.error('Une erreur est survenue. Veuillez réessayer.');
    }
  }

  const handleSuccessOk = () => {
    closeModal();
    window.location.href = `${env.apiUrl}/guest/listing/reservations`;
  };
  /*
   * ----------------------------------------------------------
   * LIBELLÉS
   * ----------------------------------------------------------
   */

  const dateLabel =
    selectedDates[0] && selectedDates[1]
      ? `${selectedDates[0].format('DD/MM')} → ${selectedDates[1].format('DD/MM')}`
      : 'Choisir dates';

  const addressLabel = urlAddress || 'Localisation';

  const compteur = selectedVoitures.length;

  /*
   * La carte desktop ne se re-rend que quand ses données changent
   * (et non à chaque ouverture de panneau, frappe clavier, etc.).
   */
  const desktopMap = useMemo(() => {
    if (isLoading) return <Skeleton height={400} />;

    return (
      <MapItem
        centers={annonces}
        centercoords={[urlLatitude, urlLongitude]}
        Ischanged={isChanged}
        onAjouterDeman={refreshSelection}
      />
    );
  }, [isLoading, annonces, urlLatitude, urlLongitude, isChanged, refreshSelection]);

  return (
    <>
      <ToastContainer />

      {/* =====================================================
          BARRE DE RECHERCHE MOBILE
          ===================================================== */}

      <div
        className="d-block d-md-none sticky-top bg-white border-bottom py-2 px-3"
        style={{ zIndex: 1020 }}
      >
        <div className="d-flex gap-2 align-items-center">
          <button
            type="button"
            className="btn btn-light btn-sm border rounded-pill d-flex align-items-center gap-1 px-3 text-start"
            style={{ maxWidth: '50%', overflow: 'hidden' }}
            onClick={() => openPanel('location')}
          >
            <i className="bi bi-geo-alt-fill text-primary flex-shrink-0" />
            <span className="text-truncate small">{addressLabel}</span>
          </button>

          <button
            type="button"
            className="btn btn-light btn-sm border rounded-pill d-flex align-items-center gap-1 px-3 flex-grow-1"
            onClick={() => openPanel('location')}
          >
            <i className="bi bi-calendar3 text-primary flex-shrink-0" />
            <span className="text-truncate small">{dateLabel}</span>
          </button>

          <button
            type="button"
            className={`btn btn-sm border rounded-pill px-3 position-relative ${
              hasFilter ? 'btn-primary text-white' : 'btn-light'
            }`}
            onClick={() => openPanel('filters')}
          >
            <i className="bi bi-sliders" />

            {hasFilter && (
              <span
                className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                style={{ fontSize: 9 }}
              >
                !
              </span>
            )}
          </button>
        </div>
      </div>

      {/* =====================================================
          BARRE DESKTOP
          ===================================================== */}

      <section className="d-none d-md-block bg-body-tertiary px-3 pt-2">
        <div className="row align-items-end">
          <div className="col-md-3 mb-2">
            <AutocompleteMapInput handleSelectLocation={handleLocationSelection} />
          </div>

          <div className="col-md-4">
            <McDateRangePicker value={selectedDates} onChange={handleDateChange} />
          </div>
        </div>

        <div className="row mb-2 gx-2 pt-1">
          <div className="col-md-3 mb-2">
            <select
              className="form-select form-select-sm"
              value={sortOrder}
              onChange={handleSortChange}
            >
              <option value="1">Peu importe</option>
              <option value="2">Prix croissant</option>
              <option value="3">Prix décroissant</option>
            </select>
          </div>

          <div className="col-md-3 mb-2">
            <select
              className="form-select form-select-sm"
              value={sortKm}
              onChange={handleSortKmChange}
            >
              <option value="1">Peu importe</option>
              <option value="2">Distance croissante</option>
              <option value="3">Distance décroissante</option>
            </select>
          </div>

          <div className="col-md-2">
            <button
              type="button"
              className="btn btn-primary text-white w-100 btn-sm"
              onClick={() => openModal('moreFilter')}
            >
              <i className="bi bi-filter-square me-1" />
              Filtres
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          CONTENU PRINCIPAL
          ===================================================== */}

      <section className="bg-body-tertiary">
        <div className="row g-0">
          {/* Liste */}
          <div className="col-12 col-md-6 px-3" style={{ paddingBottom: '148px' }}>
            <div className="d-flex align-items-center justify-content-between py-2">
              <div>
                <h2 className="h5 mb-0 fw-bold">
                  {isLoading
                    ? 'Recherche en cours…'
                    : total > 0
                      ? `${total} voiture${total > 1 ? 's' : ''} disponible${total > 1 ? 's' : ''}`
                      : 'Aucune voiture disponible'}
                </h2>

                <small className="text-muted">Dans ce secteur</small>
              </div>

              <button
                type="button"
                className="btn btn-primary btn-sm text-white d-none d-md-block position-relative"
                onClick={() => openPanel('selected')}
              >
                Mes demandes
                {compteur > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {compteur}
                  </span>
                )}
              </button>
            </div>

            <hr className="my-1" />

            <div className="annonce-list">
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="mb-3">
                    <Skeleton height={130} borderRadius={12} />
                  </div>
                ))
              ) : annonces.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-car-front text-muted" style={{ fontSize: '48px' }} />

                  <p className="text-muted mt-3">
                    Aucune annonce sur ce secteur. Essayez une autre adresse.
                  </p>
                </div>
              ) : (
                annonces.map((annonce) => (
                  <AnnonceItem
                    key={annonce.vehiculeAnnonceId}
                    propsData={annonce}
                    searchStart={searchStartDate}
                    searchEnd={searchEndDate}
                    currentUserId={currentUserId}
                    isSelected={selectedIds.has(annonce.Vehicle?.id)}
                    isFavorite={favoriteIds.has(annonce.vehiculeAnnonceId)}
                    onToggleSelect={toggleSelect}
                    onToggleFavorite={toggleFavorite}
                  />
                ))
              )}

              {loadingMore && (
                <div className="mb-3">
                  <Skeleton height={130} borderRadius={12} />
                </div>
              )}

              {/* Déclenche le chargement de la page suivante */}
              {hasMore && !isLoading && <div ref={sentinelRef} style={{ height: 1 }} />}
            </div>

            {/* Envoi desktop */}
            <div className="d-none d-md-block mt-3">
              <button
                type="button"
                className="btn btn-outline-primary w-100"
                onClick={() => openModal('reservation')}
                disabled={selectedVoitures.length === 0}
              >
                <i className="bi bi-lightning-fill me-1" />
                Envoyer une demande
              </button>
            </div>
          </div>

          {/* Carte desktop */}
          <div className="col-md-6 p-0 d-none d-md-block colmd6map">
            <h2 className="h5 px-3 py-2">Carte</h2>

            {desktopMap}
          </div>
        </div>
      </section>

      {/* =====================================================
          BARRE FIXE MOBILE
          ===================================================== */}

      <div
        className="d-block d-md-none bg-white border-top px-3 py-2"
        style={{
          position: 'fixed',
          bottom: 'calc(64px + env(safe-area-inset-bottom))',
          left: 0,
          right: 0,
          zIndex: 1035,
        }}
      >
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary flex-grow-1"
            onClick={() => openModal('fullScreenMap')}
          >
            <i className="bi bi-map me-1" />
            Carte
          </button>

          <button
            type="button"
            className="btn btn-outline-primary position-relative flex-grow-1"
            onClick={() => openPanel('selected')}
          >
            <i className="bi bi-list-check me-1" />
            Mes demandes
            {compteur > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                {compteur}
              </span>
            )}
          </button>

          {compteur > 0 && (
            <button
              type="button"
              className="btn btn-primary text-white flex-grow-1"
              onClick={() => openModal('reservation')}
            >
              <i className="bi bi-lightning-fill me-1" />
              Envoyer ({compteur})
            </button>
          )}
        </div>
      </div>

      {/* =====================================================
          PANEL LOCATION / DATES
          ===================================================== */}

      {activePanel === 'location' && (
        <>
          <div
            onClick={closePanel}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1040 }}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-label="Modifier la recherche"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              background: '#fff',
              zIndex: 1050,
              borderRadius: '16px 16px 0 0',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 -8px 30px rgba(0,0,0,.18)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 className="mb-0">Modifier la recherche</h5>

              <button
                type="button"
                className="btn-close"
                onClick={closePanel}
                aria-label="Fermer"
              />
            </div>

            <div className="p-3 pb-4">
              <label className="form-label fw-semibold small">Adresse</label>

              <AutocompleteMapInput handleSelectLocation={handleLocationSelection} />

              <label className="form-label fw-semibold small mt-3">Dates</label>

              <div style={{ overflowX: 'auto' }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateRangePicker
                    localeText={{ start: 'Début', end: 'Fin' }}
                    value={selectedDates}
                    onChange={handleDateChange}
                    disablePastDates
                  />
                </LocalizationProvider>
              </div>

              <button
                type="button"
                className="btn btn-primary text-white w-100 mt-3"
                onClick={handleSearch}
              >
                Appliquer
              </button>
            </div>
          </div>
        </>
      )}

      {/* =====================================================
          PANEL FILTRES MOBILE
          ===================================================== */}

      {activePanel === 'filters' && (
        <>
          <div
            onClick={closePanel}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1040 }}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-label="Trier et filtrer"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              background: '#fff',
              zIndex: 1050,
              borderRadius: '16px 16px 0 0',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 -8px 30px rgba(0,0,0,.18)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 className="mb-0">Trier et filtrer</h5>

              <button
                type="button"
                className="btn-close"
                onClick={closePanel}
                aria-label="Fermer"
              />
            </div>

            <div className="p-3 pb-4">
              <label className="form-label fw-semibold small">Prix</label>

              <select className="form-select mb-3" value={sortOrder} onChange={handleSortChange}>
                <option value="1">Peu importe</option>
                <option value="2">Prix croissant</option>
                <option value="3">Prix décroissant</option>
              </select>

              <label className="form-label fw-semibold small">Distance</label>

              <select className="form-select mb-3" value={sortKm} onChange={handleSortKmChange}>
                <option value="1">Peu importe</option>
                <option value="2">Plus proche</option>
                <option value="3">Plus loin</option>
              </select>

              <label className="form-label fw-semibold small">Type de véhicule</label>

              <select
                className="form-select mb-3"
                value={brand === null ? '99' : String(brand)}
                onChange={handleBrandChange}
              >
                <option value="99">Tous</option>
                <option value="0">Citadin</option>
                <option value="1">Utilitaire</option>
                <option value="2">SUV</option>
                <option value="3">Monospace</option>
              </select>

              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="mobileAcceptanceFilter"
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                />

                <label className="form-check-label small" htmlFor="mobileAcceptanceFilter">
                  Taux d'acceptation &gt; 30 %
                </label>
              </div>

              <button
                type="button"
                className="btn btn-primary text-white w-100"
                onClick={closePanel}
              >
                Appliquer les filtres
              </button>
            </div>
          </div>
        </>
      )}

      {/* =====================================================
          PANEL VOITURES SÉLECTIONNÉES
          ===================================================== */}

      {activePanel === 'selected' && (
        <>
          <div
            onClick={closePanel}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1040 }}
          />

          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Voitures sélectionnées"
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '320px',
              maxWidth: '90vw',
              background: '#fff',
              zIndex: 1050,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-8px 0 30px rgba(0,0,0,.18)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 className="mb-0">Voitures sélectionnées</h5>

              <button
                type="button"
                className="btn-close"
                onClick={closePanel}
                aria-label="Fermer"
              />
            </div>

            <div className="p-3" style={{ flex: 1, overflowY: 'auto' }}>
              {selectedVoitures.length === 0 ? (
                <p className="text-muted text-center mt-4">Aucune voiture sélectionnée.</p>
              ) : (
                <>
                  <div className="voiture-list">
                    {selectedVoitures.map((voiture, index) => (
                      <div
                        key={voiture.vehiculeAnnonceId || voiture.id_de_la_voiture || index}
                        className="d-flex align-items-center gap-2 mb-3"
                      >
                        <img
                          src={voiture.image_principal}
                          alt="Voiture"
                          style={{
                            width: '72px',
                            height: '56px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                          }}
                        />

                        <div className="flex-grow-1">
                          <p className="mb-0 small fw-semibold">Voiture #{index + 1}</p>

                          <p className="mb-0 text-muted small">{voiture.prix_par_jour} €/j</p>
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeSelected(voiture.id_de_la_voiture)}
                          aria-label="Retirer cette voiture"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary text-white w-100 mt-2"
                    onClick={() => openModal('reservation')}
                  >
                    <i className="bi bi-lightning-fill me-1" />
                    Envoyer une demande
                  </button>
                </>
              )}
            </div>
          </aside>
        </>
      )}

      {/* =====================================================
          MODALE FILTRES AVANCÉS
          ===================================================== */}

      {activeModal === 'moreFilter' && (
        <>
          <div
            onClick={closeModal}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="moreFilterTitle"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'min(500px, calc(100vw - 32px))',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: '12px',
              zIndex: 1050,
              boxShadow: '0 15px 50px rgba(0,0,0,.25)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 id="moreFilterTitle" className="mb-0">
                Filtres avancés
              </h5>

              <button
                type="button"
                className="btn-close"
                onClick={closeModal}
                aria-label="Fermer"
              />
            </div>

            <div className="p-3">
              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="desktopAcceptanceFilter"
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                />

                <label
                  className="form-check-label"
                  htmlFor="desktopAcceptanceFilter"
                  style={{ fontSize: '13px' }}
                >
                  Taux d'acceptation supérieur à 30 %
                </label>
              </div>

              <hr />

              <label className="form-label small fw-semibold">Type de véhicule</label>

              <select
                className="form-select mb-3"
                value={brand === null ? '99' : String(brand)}
                onChange={handleBrandChange}
              >
                <option value="99">Tous les véhicules</option>
                <option value="0">Citadin</option>
                <option value="1">Utilitaire</option>
                <option value="2">SUV</option>
                <option value="3">Monospace</option>
              </select>

              <button
                type="button"
                className="btn btn-primary text-white w-100"
                onClick={closeModal}
              >
                Appliquer
              </button>
            </div>
          </div>
        </>
      )}

      {/* =====================================================
          MODALE CARTE PLEIN ÉCRAN
          (montée uniquement à l'ouverture)
          ===================================================== */}

      {activeModal === 'fullScreenMap' && (
        <>
          <div
            onClick={closeModal}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1040 }}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-label="Carte"
            style={{
              position: 'fixed',
              inset: 0,
              background: '#fff',
              zIndex: 1050,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 className="mb-0">Carte</h5>

              <button
                type="button"
                className="btn-close"
                onClick={closeModal}
                aria-label="Fermer"
              />
            </div>

            <div className="p-0" style={{ flex: 1, minHeight: 0 }}>
              <MapItem
                centers={annonces}
                centercoords={[urlLatitude, urlLongitude]}
                onAjouterDeman={refreshSelection}
              />
            </div>
          </div>
        </>
      )}

      {/* =====================================================
          MODALE CONFIRMATION RÉSERVATION
          ===================================================== */}

      {activeModal === 'reservation' && (
        <>
          <div
            onClick={closeModal}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reservationModalTitle"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'min(500px, calc(100vw - 32px))',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: '12px',
              zIndex: 1050,
              boxShadow: '0 15px 50px rgba(0,0,0,.25)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 id="reservationModalTitle" className="mb-0">
                Confirmation de réservation
              </h5>

              <button
                type="button"
                className="btn-close"
                onClick={closeModal}
                aria-label="Fermer"
              />
            </div>

            <div className="p-3">
              <p className="text-muted small">
                Ajoutez un message pour le propriétaire (facultatif).
              </p>

              <textarea
                className="form-control"
                rows="4"
                placeholder="Entrez votre message ici…"
                onChange={(event) => setPersonnalisedMessage(event.target.value)}
                value={personnalisedMessage}
              />
            </div>

            <div className="d-flex gap-2 p-3 border-top">
              <button type="button" className="btn btn-outline-secondary" onClick={closeModal}>
                Annuler
              </button>

              <button
                type="button"
                className="btn btn-primary text-white flex-grow-1"
                onClick={handleReserver}
                disabled={selectedVoitures.length === 0}
              >
                <i className="bi bi-lightning-fill me-1" />
                Confirmer
              </button>
            </div>
          </div>
        </>
      )}

      {/* =====================================================
    MODALE SUCCÈS ENVOI DE DEMANDE
    ===================================================== */}

      {activeModal === 'success' && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="successModalTitle"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'min(500px, calc(100vw - 32px))',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: '12px',
              zIndex: 1050,
              boxShadow: '0 15px 50px rgba(0,0,0,.25)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <h5 id="successModalTitle" className="mb-0">
                Demande envoyée
              </h5>

              <button
                type="button"
                className="btn-close"
                onClick={handleSuccessOk}
                aria-label="Fermer"
              />
            </div>

            <div className="p-3 text-center">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '48px' }} />

              <p className="mt-3 mb-1 fw-semibold">Votre message a été envoyé avec succès !</p>

              <p className="text-muted small mb-0">
                Vous serez notifié dès qu'un hôte accepte votre demande.
              </p>
            </div>

            <div className="p-3 border-top">
              <button
                type="button"
                className="btn btn-primary text-white w-100"
                onClick={handleSuccessOk}
              >
                OK
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default SearchResultPage;
